import {AB_BRAND, AB_STATUS, AB_SURFACES} from '@s/app_colors';
import {DensityPreference} from '@shared/enums';
import type {DefaultsInstance, ThemeDefinition} from 'vuetify';

/**
 * Brand themes, replacing Vuetify's stock `light` and `dark` under the same
 * names. Values come from app_colors.ts.
 *
 * Every entry in `colors` makes Vuetify emit three CSS variables and three
 * utility classes per theme (vuetify@4.1.3); add colors deliberately. The
 * SCSS tokens in variables.scss wrap them for component styles.
 */
const abLightTheme: ThemeDefinition = {
   dark: false,
   colors: {
      background: AB_SURFACES.background.light,
      'on-background': AB_SURFACES.text.light,
      surface: AB_SURFACES.surface.light,
      'on-surface': AB_SURFACES.text.light,
      'surface-bright': AB_SURFACES.surface.light,
      'surface-light': AB_SURFACES.surfaceLight.light,
      // Inverse surface (tooltips) + its light text
      'surface-variant': AB_BRAND.neutral.n700,
      'on-surface-variant': AB_BRAND.neutral.n100,

      primary: AB_BRAND.blue.std,
      'primary-darken-1': AB_BRAND.blue.deep,
      // Brand teal slot; not a chrome accent (design_language/design_language.md)
      secondary: AB_BRAND.teal.std,
      'secondary-darken-1': AB_BRAND.teal.deep,
      // Violet: AI/automation and premium moments only
      accent: AB_BRAND.violet.std,

      info: AB_STATUS.info.light,
      success: AB_STATUS.success.light,
      warning: AB_STATUS.warning.light,
      error: AB_STATUS.error.light,

      // Selected toggles/rows background
      selection: AB_SURFACES.selection.light,
      // Fixed brand slots for the bg-/text-brand-* utility classes
      'brand-blue': AB_BRAND.blue.std,
      'brand-royal': AB_BRAND.royal.std,
      'brand-violet': AB_BRAND.violet.std,
      'brand-teal': AB_BRAND.teal.std,
   },
   variables: {
      'border-color': AB_BRAND.neutral.n900,
   },
};

const abDarkTheme: ThemeDefinition = {
   dark: true,
   colors: {
      background: AB_SURFACES.background.dark,
      'on-background': AB_SURFACES.text.dark,
      surface: AB_SURFACES.surface.dark,
      'on-surface': AB_SURFACES.text.dark,
      'surface-bright': AB_SURFACES.surfaceLight.dark,
      'surface-light': AB_SURFACES.surfaceLight.dark,
      'surface-variant': AB_BRAND.neutral.n300,
      'on-surface-variant': AB_BRAND.neutral.n950,

      primary: AB_BRAND.blue.strong,
      'primary-darken-1': AB_BRAND.blue.strongDeep,
      secondary: AB_BRAND.teal.light,
      'secondary-darken-1': AB_BRAND.teal.std,
      accent: AB_BRAND.violet.light,

      info: AB_STATUS.info.dark,
      success: AB_STATUS.success.dark,
      warning: AB_STATUS.warning.dark,
      error: AB_STATUS.error.dark,

      // Deep blue counterpart of the light theme's pale-blue selection
      selection: AB_SURFACES.selection.dark,
      // Lighter variants keep the brand colors legible on dark surfaces.
      'brand-blue': AB_BRAND.blue.strong,
      'brand-royal': AB_BRAND.royal.light,
      'brand-violet': AB_BRAND.violet.light,
      'brand-teal': AB_BRAND.teal.light,
   },
   variables: {
      'border-color': AB_BRAND.neutral.n100,
   },
};

/** Theme set for createVuetify(); shared by the main app and the mobile app. */
export const appThemes: Record<string, ThemeDefinition> = {
   light: abLightTheme,
   dark: abDarkTheme,
};

/**
 * Build the Vuetify component defaults for the given UI density: variants,
 * densities, colors and nested overrides. Entries for components an app never
 * renders are ignored. `VSelectorBtnToggle` and `VSmallSelectorBtnToggle` are
 * aliases registered only by the main app's createVuetify(); the mobile app
 * ignores them.
 *
 * An explicit density prop in a template overrides these; leave the prop off
 * where the user's density preference should apply.
 */
export function buildAppVuetifyDefaults(density: DensityPreference): DefaultsInstance {
   // Compact is the product default (design_language/design_language.md); COMFORTABLE is the opt-out.
   const is_comfortable = density === DensityPreference.COMFORTABLE;

   const standard_density = is_comfortable ? 'comfortable' : 'compact';
   // Lists get no density default in comfortable mode.
   const list_density = is_comfortable ? undefined : 'compact';

   return {
      global: {
         density: standard_density,
      },
      VAlert: {
         variant: 'tonal',
         density: standard_density,
         VIcon: {
            size: undefined,
            // allow valert to set the color
            color: undefined,
         },
      },

      // Global component defaults
      VTextField: {
         variant: 'outlined',
         density: standard_density,

         // Icons in text fields prepend/append should be small
         VIcon: {
            size: 'x-small',
         },
      },
      // Do not override VBtn's own props; it is used too widely

      VBtn: {
         VIcon: {
            color: 'inherit',
         },
      },

      // The one filled-blue action per view (design_language/design_language.md)
      VBtnPrimary: {
         color: 'primary',
         variant: 'flat',
      },
      // Outlined container at container radius (design_language/design_language.md)
      VCardPanel: {
         variant: 'outlined',
         rounded: 'lg',
      },

      VSelectorBtnToggle: {
         density: 'compact',
         border: 'sm',
         divided: true,
         baseColor: 'surface-light',
         color: 'selection',
         variant: 'elevated',
      },
      // A smaller VBtnToggle: fixed height with small buttons
      VSmallSelectorBtnToggle: {
         density: 'compact',
         border: 'sm',
         divided: true,
         baseColor: 'surface-light',
         color: 'selection',
         variant: 'elevated',
         style: 'height: 28px;',
         VBtn: {
            size: 'small',
         },
      },
      VIconBtn: {
         variant: 'default',
         ripple: true,
         size: 'small',
         // Muted icon gray, theme-aware (follows light/dark surface text color)
         color: 'rgba(var(--v-theme-on-surface), 0.54)',
      },
      VSwitch: {
         color: 'primary',
         hideDetails: true,
      },
      VSelect: {
         variant: 'outlined',
         density: standard_density,
         autocomplete: 'suppress',
         VChip: {
            variant: 'tonal',
         },
      },
      VAutocomplete: {
         variant: 'outlined',
         density: standard_density,
         autocomplete: 'suppress',
         clearOnSelect: true,
         VChip: {
            variant: 'tonal',
         },
      },
      VCombobox: {
         variant: 'outlined',
         density: standard_density,
         VChip: {
            variant: 'tonal',
         },
      },
      VTextarea: {
         variant: 'outlined',
         density: standard_density,
      },
      VDateInput: {
         variant: 'outlined',
         density: standard_density,
         prependIcon: '',
         prependInnerIcon: '$calendar',
      },
      VIcon: {
         size: 'small',
         // Muted icon gray, theme-aware (follows light/dark surface text color)
         color: 'rgba(var(--v-theme-on-surface), 0.54)',
      },
      VChip: {
         variant: 'flat',
         // Tag shape, not pill (design_language/design_language.md)
         label: true,
      },
      VChipGroup: {
         variant: 'tonal',
         // Selection is a blue state (design_language/design_language.md)
         color: 'primary',
         VChip: {
            density: standard_density,
            //variant: 'tonal',
         },
      },
      VProgressLinear: {
         color: 'primary',
      },
      VList: {
         density: list_density,
      },

      // -- COMMON GROUPINGS -- //
      VCard: {
         VCardActions: {
            VBtn: {
               variant: 'tonal',
            },
         },
      },

      // note: can't use nested because props aren't pushed through
      VDataTable: {
         density: standard_density,

         VPagination: {
            density: 'compact',
            size: 'small',
            showFirstLastPage: false,
         },
      },

      VDataTableRow: {
         // Expansion button
         VBtn: {
            color: 'rgba(var(--v-theme-on-surface), 0.54)',
         },
         // Action icons
         VIconBtn: {
            color: 'rgba(var(--v-theme-on-surface), 0.54)',
            VIcon: {
               size: 'x-small',
            },
         },
      },

      VTabs: {
         density: 'compact',
         VTab: {
            size: 'small',
         },
      },

      VNavigationDrawer: {
         // Chrome carries the tint; content stays white (design_language/design_language.md)
         color: 'surface-light',
         VIcon: {
            size: 'x-small',
         },
      },
   };
}

/** The defaults at the user's default density; createVuetify() starts from these. */
export const appVuetifyDefaults: DefaultsInstance = buildAppVuetifyDefaults(
   DensityPreference.DEFAULT,
);
